import BaseClient from "./BaseClient";

export default class SearchServiceClient extends BaseClient {
    constructor(tenantId: string) {
        super(null, tenantId, true, "ams");
    }
    public async getAccountTransactions(accountId: string): Promise<SearchResponse> {
        const response = await this.get(`/api/proxy/ams/search-service/account/${accountId}/transactions?top=50&skip=0`, { 'Api-Version': 2});
        return await this.handleResponse<SearchResponse>(response, true);
    }

    public async getAccountPayments(accountId: string): Promise<TransactionResult[]> {
        return this.getAllAccountTransactions(accountId, 'Payment');
    }

    public async getOtherTransactions(accountId: string): Promise<TransactionResult[]> {
        const transactions = await this.getAllAccountTransactions(accountId);
        return transactions.filter((transaction) => transaction.balanceMovementType !== 'Payment');
    }

    private async getAllAccountTransactions(accountId: string, balanceMovementType?: string): Promise<TransactionResult[]> {
        const transactions: TransactionResult[] = [];
        const pageSize = 50;
        let skip = 0;
        let hasMore: boolean;

        do {
            const filter = balanceMovementType ? `&balanceMovementType=${encodeURIComponent(balanceMovementType)}` : '';
            const response = await this.get(
                `/api/proxy/ams/search-service/account/${accountId}/transactions?top=${pageSize}&skip=${skip}${filter}`,
                { 'Api-Version': 2 }
            );
            if (!response.ok) {
                throw new Error(`Failed to load transactions: HTTP ${response.status}`);
            }

            const page = await this.handleResponse<SearchResponse>(response);
            if (!page || !Array.isArray(page.value) || (page.nextLink !== null && typeof page.nextLink !== 'string')) {
                throw new Error('Invalid transaction search response');
            }

            transactions.push(...page.value);
            hasMore = !!page.nextLink;
            if (hasMore && page.value.length === 0) {
                throw new Error('Transaction search returned an empty page with more results');
            }
            skip += pageSize;
        } while (hasMore);

        return transactions;
    }

    public async getTransactionsForBillingPeriod(accountId: string, billingPeriod: number, top: number, skip: number): Promise<SearchResponse> {
        const response = await this.get(`/api/proxy/ams/search-service/account/${accountId}/transactions?top=${top}&skip=${skip}&billingPeriodId=${billingPeriod}`, { 'Api-Version': 2});
        return await this.handleResponse<SearchResponse>(response, true);
    }
}

export const useSearchService = (tenantId: string): SearchServiceClient => {
    return new SearchServiceClient(tenantId);
};


type SearchResponse =
{ 
    value: TransactionResult[],
    nextLink: string | null
}

export type TransactionResult =
{
    transactionId: string,
    amount: Amount,
    upfrontPaymentAmount?: Amount,
    description: string | null,
    balanceMovementType: string,
    effectiveDate: string,
    postingDate: string | null,
    transactionDate: string | null,
    transactionStatus: string | null,
    creditRelease: string | null,
    isCreditReleased: boolean | null,
    direction: string
}